import { Injectable } from '@angular/core';
import Bugsnag, { NotifiableError, OnErrorCallback } from '@bugsnag/js';
import { LDFlagSet } from 'launchdarkly-js-client-sdk';
import { isAlreadyReported, markReported } from './recently-reported';
import { getLatestUserRequestTraceId, recordRequestTraceId } from './request-trace-ids';

/**
 * Wrapper around Bugsnag so app code doesn't call the SDK directly.
 * Every method is a no-op until `startBugsnag` has run.
 */
@Injectable({ providedIn: 'root' })
export class ErrorTrackingService {
  public notify(error: NotifiableError, onError?: OnErrorCallback): void {
    if (Bugsnag.isStarted()) {
      Bugsnag.notify(error, onError);
    }
  }

  /** Records a message already sent to Bugsnag so a rethrow of the same message isn't reported again. */
  public markReported(message: string): void {
    markReported(message);
  }

  /** True for failures Bugsnag already has (see `isAlreadyReported` in recently-reported.ts). */
  public isAlreadyReported(error: unknown): boolean {
    return isAlreadyReported(error);
  }

  /** Lets the Bugsnag request breadcrumb for this request carry its Datadog trace ID. */
  public recordRequestTraceId(method: string, url: string, traceId: string): void {
    recordRequestTraceId(method, url, traceId);
  }

  /** Datadog trace ID of the latest `GET /api/user`, or undefined if none was traced. */
  public get latestUserRequestTraceId(): string | undefined {
    return getLatestUserRequestTraceId();
  }

  /** Tags every event with `simulation.simulated: true` (admin scenario simulator). */
  public setSimulated(simulated: boolean): void {
    if (Bugsnag.isStarted()) {
      Bugsnag.addMetadata('simulation', { simulated });
    }
  }

  /** Leaves a manual breadcrumb on events reported after this point. */
  public addBreadcrumb(message: string, metadata: Record<string, unknown> = {}): void {
    if (Bugsnag.isStarted()) {
      Bugsnag.leaveBreadcrumb(message, metadata, 'manual');
    }
  }

  /**
   * Identifies the user by the same ID the app sends to Amplitude. Never pass a name or email.
   * `metadata` (e.g. `{ userType }`) goes on the `user` tab.
   */
  public setUser(userId: string, metadata: Record<string, unknown> = {}): void {
    if (!Bugsnag.isStarted()) {
      return;
    }
    Bugsnag.setUser(userId);
    if (Object.keys(metadata).length > 0) {
      Bugsnag.addMetadata('user', metadata);
    }
  }

  public setLocation(locationNumber: string): void {
    if (Bugsnag.isStarted()) {
      Bugsnag.addMetadata('location', { locationNumber });
    }
  }

  /** Replaces the feature flags attached to every event with the current LaunchDarkly flags. */
  public setFeatureFlags(flags: LDFlagSet): void {
    if (!Bugsnag.isStarted()) {
      return;
    }
    Bugsnag.clearFeatureFlags();
    Bugsnag.addFeatureFlags(Object.entries(flags ?? {}).map(([name, value]) => ({
      name,
      variant: ['string', 'number', 'boolean'].includes(typeof value) ? String(value) : null
    })));
  }
}
